import { calculateCategoryTabStyle } from 'components/categories/CategoryTabList';
import { useRouter } from 'next/router';
import React, { useState, useRef, useEffect, useMemo } from 'react';
import Tabs, { TabList, Tab } from '@seaweb/coral/components/Tabs';
import styled from 'styled-components';
import DropdownIcon from '@seaweb/coral/components/icons/Dropdown';
import ArrowUpIcon from '@seaweb/coral/components/icons/ArrowUp';
import { array, bool, object } from 'prop-types';

const StyledTabs = styled(Tabs)`
  z-index: 1;
  ${calculateCategoryTabStyle}
  ${(props) =>
    props.beyondThreshold
      ? `
        box-shadow: 0px 16px 10px rgba(102, 138, 184, 0.1);
        transition: all 0.15s ease;`
      : ''}
`;

const MobileMenuDropdown = styled.div`
  ${({ open }) =>
    open
      ? `transform: translate(0px, 0px);`
      : `transform: translate(0px, -50px);`}
  ${({ open }) => (open ? `max-height: 500px;` : `max-height: 0px;`)}
  width: 100%;
  overflow: hidden;
  position: absolute;
  top: 56px;
  left: 0;
  z-index: 3;
  box-shadow: 0 16px 30px 0 rgba(102, 138, 184, 0.1);
  transition: all 0.2s ease;
  background-color: white;

  @media screen and (max-width: 600px) {
    & + * {
      display: none !important;
    }
  }
`;

const SelectedDiv = styled.div`
  height: 56px;
  width: 100%;
  display: flex;
  justify-content: space-between;
  background-color: #f9fbfe;
  z-index: 4;
`;

const FirstTab = styled(Tab)`
  height: 100%;
  width: 100%;
  color: #202325 !important;
  font-size: 14px;
  font-weight: 600;
  display: flex;
  justify-content: space-between;
  align-items: center;

  &:focus {
    color: #202325;
  }
`;

const StyledTab = styled(Tab)`
  height: 56px;
  width: 100%;
  background-color: #f9fbfe;
  color: #202325;
  font-size: 14px;
  display: flex;
  justify-content: flex-start;

  &:focus {
    color: #202325;
  }
`;

const StyledArrowUpIcon = styled(ArrowUpIcon)`
  width: 20px;
  height: 20px;
  object-fit: contain;
  opacity: 0.2;
`;

const StyledDropdownIcon = styled(DropdownIcon)`
  width: 20px;
  height: 20px;
  object-fit: contain;
  opacity: 0.2;
`;

const CategoryTabListMobile = ({
  categories,
  currentCategory,
  isScrollDown,
  beyondThreshold,
  notHiddenWhenScrollDown,
}) => {
  const { push } = useRouter();
  const [mobileTabListOpen, setMobileTabListOpen] = useState(false);
  const selectedRef = useRef(null);
  const categoryIds = useMemo(() => {
    return categories.map((category) => category.id);
  }, [categories]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (!selectedRef.current.contains(event.target)) {
        setMobileTabListOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  });

  const handleOpen = () => {
    setMobileTabListOpen(true);
  };

  const handleClose = () => {
    setMobileTabListOpen(false);
  };

  const handleClick = () => {
    if (mobileTabListOpen) {
      handleClose();
    } else {
      handleOpen();
    }
  };

  return (
    <div style={{ height: '56px' }}>
      <StyledTabs
        index={categoryIds.indexOf(currentCategory.id)}
        isScrollDown={isScrollDown}
        beyondThreshold={beyondThreshold}
        notHiddenWhenScrollDown={notHiddenWhenScrollDown}
      >
        <TabList>
          <SelectedDiv onClick={handleClick} ref={selectedRef}>
            <FirstTab index={currentCategory.id} key={currentCategory.id}>
              {currentCategory.name}
              {mobileTabListOpen ? (
                <StyledArrowUpIcon />
              ) : (
                <StyledDropdownIcon />
              )}
            </FirstTab>
          </SelectedDiv>

          <MobileMenuDropdown open={mobileTabListOpen}>
            {currentCategory.subCategories.map((subCategory) => (
              <StyledTab
                index={subCategory.id}
                key={subCategory.id}
                onRelease={() => {
                  push(`/categories/${currentCategory.id}#${subCategory.id}`);
                  handleClose();
                }}
              >
                {subCategory.name}
              </StyledTab>
            ))}
          </MobileMenuDropdown>
        </TabList>
      </StyledTabs>
    </div>
  );
};

CategoryTabListMobile.propTypes = {
  currentCategory: object.isRequired,
  categories: array,
  isScrollDown: bool,
  beyondThreshold: bool,
  notHiddenWhenScrollDown: bool,
};

export default CategoryTabListMobile;
